/**
 * Run statistics + persistent best records (localStorage).
 * On run end, improved metrics are reported so the UI can show
 * a "NEW RECORD" badge. docs/01 "his" goals.
 */
export interface RunStats {
  timeAlive: number;
  bossKills: number;
  droneKills: number;
  gemsCollected: number;
  bulletsFired: number;
  damageDealt: number;
  arenasCleared: number;
}

export const STAT_KEYS: ReadonlyArray<keyof RunStats> = [
  'timeAlive',
  'bossKills',
  'droneKills',
  'gemsCollected',
  'bulletsFired',
  'damageDealt',
  'arenasCleared',
];

const STORAGE_KEY = 'bosshot-records-v1';

export function emptyStats(): RunStats {
  return {
    timeAlive: 0,
    bossKills: 0,
    droneKills: 0,
    gemsCollected: 0,
    bulletsFired: 0,
    damageDealt: 0,
    arenasCleared: 0,
  };
}

export function loadRecords(): Partial<RunStats> {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Partial<RunStats>) : {};
  } catch {
    return {};
  }
}

/**
 * Compare a finished run against stored records, persist improvements.
 * @returns the metric keys that set a new record
 */
export function updateRecords(stats: RunStats): Array<keyof RunStats> {
  const records = loadRecords();
  const improved: Array<keyof RunStats> = [];

  for (const key of STAT_KEYS) {
    const best = records[key] ?? 0;
    if (stats[key] > best) {
      records[key] = stats[key];
      improved.push(key);
    }
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(records));
  } catch {
    /* storage unavailable — ignore */
  }
  return improved;
}

export function formatStat(key: keyof RunStats, value: number): string {
  if (key === 'timeAlive') {
    const m = Math.floor(value / 60);
    const s = Math.floor(value % 60);
    return `${m}:${String(s).padStart(2, '0')}`;
  }
  return String(Math.round(value));
}
